import { openPopup } from '@neb/popup';
import { LitElement, html, css } from 'lit';
import '../../../../neb-lit-components/src/components/controls/neb-tab-group';
import '../../../../neb-lit-components/src/components/neb-tooltip';

import './neb-my-statements';
import './neb-my-receipts';
import { getBookingBillingAddress } from '../../../../../src/api-clients/booking/booking-billing-address';
import { getOnlinePatientBalance } from '../../../../../src/api-clients/booking/booking-patient';
import { navigate } from '../../../../../src/store';
import {
  CSS_COLOR_HIGHLIGHT,
  CSS_FONT_SIZE_CAPTION,
  CSS_FONT_SIZE_HEADER,
  CSS_FONT_WEIGHT_BOLD,
  CSS_SPACING,
} from '../../../../../src/styles';
import { POPUP_RENDER_KEYS } from '../../../../neb-popup/src/renderer-keys';
import { baseStyles } from '../../../../neb-styles/neb-styles';
import { formatDollarAmountWithNegative } from '../../../../neb-utils/formatters';

export const ELEMENTS = {
  balanceText: { id: 'balance' },
  paymentButton: { id: 'payment-button' },
  tabs: { id: 'tabs' },
  myStatementsPage: { id: 'my-statements-page' },
  myReceiptsPage: { id: 'my-receipts-page' },
  negativeTooltip: { id: 'negative-tooltip' },
};

class NebMyPayments extends LitElement {
  static get properties() {
    return {
      layout: {
        type: String,
        reflect: true,
      },
      __currentBalance: Number,
      __billingAddress: Object,
      __selectedTab: String,
      linkedPatient: Object,
      merchantAccount: Object,
      practiceLogoSrc: String,
      practiceInfo: Object,
    };
  }

  static get styles() {
    return [
      baseStyles,
      css`
        .title {
          color: ${CSS_COLOR_HIGHLIGHT};
          font-size: ${CSS_FONT_SIZE_HEADER};
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          padding-top: 30px;
        }

        :host([layout='small']) .title {
          padding: 30px ${CSS_SPACING} 0px;
        }

        .balance-tooltip-container {
          display: flex;
        }

        .balance-text {
          font-size: ${CSS_FONT_SIZE_CAPTION};
          padding-top: 10px;
        }

        .tooltip {
          padding: 8px 0 0 10px;
        }

        :host([layout='small']) .balance-text {
          padding-left: ${CSS_SPACING};
        }

        .action-button {
          padding: ${CSS_SPACING} 0px;
        }

        :host([layout='small']) .action-button {
          padding: ${CSS_SPACING};
        }

        .tabs {
          flex: 0 0 auto;
          padding-top: 12px;
        }
      `,
    ];
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
  }

  __genNavItems() {
    return [
      {
        id: 'statements',
        label: 'Statements',
        resolver: () => this.__renderStatements(),
      },
      {
        id: 'receipts',
        label: 'Receipts',
        resolver: () => this.__renderReceipts(),
      },
    ];
  }

  __initState() {
    this.__currentBalance = 0;
    this.__selectedTab = this.__genNavItems()[0].id;

    this.merchantAccount = {};
    this.layout = '';
    this.linkedPatient = {};
    this.practiceInfo = {};
    this.practiceLogoSrc = '';
    this.__billingAddress = null;
  }

  __initHandlers() {
    this.__handlers = {
      makePayment: async () => {
        const payment = await openPopup(
          POPUP_RENDER_KEYS.PATIENT_ONLINE_PAYMENT,
          {
            currentBalance: this.__currentBalance,
            patient: this.linkedPatient,
            merchantAccount: this.merchantAccount,
            practiceInfo: this.practiceInfo,
            billingAddress: this.__billingAddress,
          },
        );

        if (payment) {
          await this.__fetch();

          if (this.__selectedTab === 'receipts') {
            const myReceiptsPage = this.shadowRoot.getElementById(
              ELEMENTS.myReceiptsPage.id,
            );

            myReceiptsPage.refreshTable();
          }
        }
      },
      selectTab: tab => {
        this.__selectedTab = tab;
      },
    };
  }

  async updated(changedProps) {
    if (!this.linkedPatient.patientId) {
      navigate('#/');
    } else if (changedProps.has('linkedPatient')) {
      await this.__fetch();
    }
  }

  async __fetch() {
    const { balance } = await getOnlinePatientBalance(
      this.linkedPatient.patientId,
    );
    this.__currentBalance = balance;

    const { billingAddress } = await getBookingBillingAddress();
    this.__billingAddress = billingAddress;
  }

  __renderStatements() {
    return html`
      <neb-my-statements
        id="${ELEMENTS.myStatementsPage.id}"
        .layout="${this.layout}"
        .linkedPatient="${this.linkedPatient}"
      ></neb-my-statements>
    `;
  }

  __renderReceipts() {
    return html`
      <neb-my-receipts
        id="${ELEMENTS.myReceiptsPage.id}"
        .practiceInfo="${this.practiceInfo}"
        .billingAddress="${this.__billingAddress}"
        .practiceLogoSrc="${this.practiceLogoSrc}"
        .layout="${this.layout}"
        .linkedPatient="${this.linkedPatient}"
      >
      </neb-my-receipts>
    `;
  }

  __renderTabContent() {
    const item = this.__genNavItems().find(
      ({ id }) => id === this.__selectedTab,
    );

    return item.resolver();
  }

  __renderTabs() {
    return html`
      <neb-tab-group
        id="${ELEMENTS.tabs.id}"
        class="tabs"
        .selectedId="${this.__selectedTab}"
        .items="${this.__genNavItems()}"
        .onSelect="${this.__handlers.selectTab}"
      >
      </neb-tab-group>
    `;
  }

  __renderTooltip() {
    return html`
      <neb-tooltip
        id="${ELEMENTS.negativeTooltip.id}"
        class="tooltip"
        defaultAnchor="right"
      >
        <div slot="tooltip">
          You have a credit of
          ${formatDollarAmountWithNegative(-this.__currentBalance)}. Payment is
          not needed at this time.
        </div>
      </neb-tooltip>
    `;
  }

  render() {
    return html`
      <div class="title">Balance</div>

      <div class="balance-tooltip-container">
        <neb-text id="${ELEMENTS.balanceText.id}" class="balance-text"
          >Your outstanding balance:
          <b
            >${formatDollarAmountWithNegative(this.__currentBalance)}</b
          ></neb-text
        >

        ${this.__currentBalance < 0 ? this.__renderTooltip() : ''}
      </div>

      <div class="action-button">
        <neb-button
          id="${ELEMENTS.paymentButton.id}"
          class="button"
          label="Make a payment"
          .onClick="${this.__handlers.makePayment}"
        ></neb-button>
      </div>
      ${this.__renderTabs()}${this.__renderTabContent()}
    `;
  }
}

customElements.define('neb-my-payments', NebMyPayments);
